/**
 * Intercepts "add to cart" clicks and opens an ATC popup (combined-listing
 * popup, post-ATC upsell popup, ...) instead, when one is present for the
 * current product.
 *
 * Popups are registered with a hidden marker inside the product scope:
 *   <span hidden data-atc-popup-id="PopupId" data-atc-popup-condition="..."></span>
 * The optional condition names an entry in CONDITIONS that must pass for the
 * popup to open. The first matching marker wins.
 *
 * Only two buttons are treated as triggers: the main PDP add-to-cart button
 * (inside [data-enhanced-atc]) and the sticky ATC bar's button (inside
 * .product__sticky-atc-wrapper). Upsell / complementary-product / other
 * quick-add buttons elsewhere on the page are intentionally not matched.
 *
 * Runs at the document level (capture phase) so it works independently of
 * product-form.js's own submit handling, and is safe to have multiple popups
 * on a page: each lookup is scoped to the closest [data-product-popup-scope]
 * ancestor (e.g. <product-info>), so one popup never interferes with another.
 *
 * Inside a popup:
 *   [data-atc-popup-decline]            closes it and adds only the original product.
 *   [data-atc-popup-accept="variantId"] closes it and adds the original product plus that variant;
 *                                       an optional [data-atc-popup-discount="20"] on the same button
 *                                       adds that variant with properties[_itemPercentageDiscount].
 *                                       [data-atc-popup-items='[{"id":1,"properties":{...}}]'] adds those lines instead
 *                                       (e.g. the BOGO popup's paid + free pair).
 *   [data-combined-listing-popup-cta]   popup's own product form; closes it once that form has added to cart.
 *
 * Chained popups: a popup with [data-atc-popup-next="PopupId"] opens that popup on accept / decline
 * instead of submitting. Accepted variants are carried along the chain, and the last popup submits
 * the original product with all of them. Closing any popup in the chain cancels the add to cart.
 * The next popup's "added to cart" bar ([data-atc-popup-cart-message]) shows the [data-atc-popup-message]
 * of the last accepted button in the chain, or its own default message.
 *
 * Tracking: an accept / decline button (or a changed input, e.g. the BOGO offer switcher) with
 * [data-eab-tracking-id] (and [data-eab-event-target]) publishes that Elevate custom event.
 */
(function () {
  var TRIGGER_SELECTOR = '[data-enhanced-atc] .product-form__submit, .product__sticky-atc-wrapper .quick-add__submit';

  var CONDITIONS = {
    'combined-listing-first': function (scope) {
      var firstInput = scope.querySelector('combined-listing [data-combined-listing-input]');
      return Boolean(firstInput && firstInput.checked);
    },
    // PDP_CF_006 | Temporary A/B test gate: the test variant's script sets this attribute on <html>. Added 'data-atc-popup-condition' attribute.
    'post-atc-upsell-ab': function () {
      return document.documentElement.hasAttribute('data-post-atc-upsell-ab');
    },
    // BFCM post-ATC flow A/B test gate: the test variant's script sets this attribute on <html>.
    'post-atc-bfcm-ab': function () {
      return document.documentElement.hasAttribute('data-post-atc-bfcm-ab');
    },
  };

  function getPopupFor(button) {
    var scope = button.closest('[data-product-popup-scope]') || button.closest('section')?.querySelector('[data-product-popup-scope]');
    if (!scope) return null;

    var markers = scope.querySelectorAll('[data-atc-popup-id]');
    for (var i = 0; i < markers.length; i++) {
      var condition = CONDITIONS[markers[i].dataset.atcPopupCondition];
      if (condition && !condition(scope)) continue;

      var popup = document.getElementById(markers[i].dataset.atcPopupId);
      if (popup) return popup;
    }

    return null;
  }

  // Elevate "Custom JavaScript Event": tracking_id and event_target come from the event's snippet in the Elevate dashboard.
  function trackElevateEvent(trackingId, eventTarget) {
    if (!trackingId || !window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') return;

    window.Shopify.analytics.publish('eab:custom_event', { tracking_id: trackingId, event_target: eventTarget || 'custom_event' });
  }

  function openPopup(popup, triggerButton, upsellItems, cartMessage) {
    popup.triggerButton = triggerButton;
    popup.upsellItems = upsellItems || [];
    popup.cartMessage = cartMessage || null;

    var messageElement = popup.querySelector('[data-atc-popup-cart-message]');
    if (messageElement) messageElement.textContent = cartMessage || messageElement.dataset.defaultMessage;

    popup.show(triggerButton);
  }

  function submitOriginalForm(popup, upsellItems) {
    popup.hide();

    var originalButton = popup.triggerButton;
    var originalForm = originalButton ? originalButton.closest('form') : null;
    if (!originalForm || typeof originalForm.requestSubmit !== 'function') return;

    var productForm = originalForm.closest('product-form');
    if (upsellItems.length && productForm) productForm.dataset.upsellItems = JSON.stringify(upsellItems);

    originalForm.requestSubmit(originalButton);
  }

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest(TRIGGER_SELECTOR);
      if (!button) return;

      var popup = getPopupFor(button);
      if (!popup) return;

      event.preventDefault();
      openPopup(popup, button);
    },
    true
  );

  document.addEventListener('click', function (event) {
    var cta = event.target.closest('[data-combined-listing-popup-cta]');
    if (!cta) return;

    var popup = cta.closest('.product-popup-modal');
    var form = cta.closest('form');
    var variantInput = form ? form.querySelector('[name="id"]') : null;
    if (!popup || !variantInput) return;

    var variantId = variantInput.value;
    var unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, function (cartEvent) {
      if (cartEvent.source !== 'product-form' || String(cartEvent.productVariantId) !== String(variantId)) return;
      popup.hide();
      unsubscribe();
    });
  });

  document.addEventListener('click', function (event) {
    var action = event.target.closest('[data-atc-popup-accept], [data-atc-popup-decline]');
    if (!action) return;

    var popup = action.closest('.product-popup-modal');
    if (!popup) return;

    trackElevateEvent(action.dataset.eabTrackingId, action.dataset.eabEventTarget);

    var upsellItems = (popup.upsellItems || []).slice();
    var cartMessage = popup.cartMessage;
    if (action.hasAttribute('data-atc-popup-accept')) {
      if (action.dataset.atcPopupItems) {
        upsellItems = upsellItems.concat(JSON.parse(action.dataset.atcPopupItems));
      } else if (action.dataset.atcPopupAccept) {
        upsellItems.push({ id: action.dataset.atcPopupAccept, discount: action.dataset.atcPopupDiscount || null });
      }
      cartMessage = action.dataset.atcPopupMessage || cartMessage;
    }

    var nextPopup = popup.dataset.atcPopupNext && document.getElementById(popup.dataset.atcPopupNext);
    if (nextPopup) {
      popup.hide();
      openPopup(nextPopup, popup.triggerButton, upsellItems, cartMessage);
      return;
    }

    submitOriginalForm(popup, upsellItems);
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('.product-popup-modal input[data-eab-tracking-id]');
    if (!input || !input.checked) return;

    trackElevateEvent(input.dataset.eabTrackingId, input.dataset.eabEventTarget);
  });
})();
